import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "../../lib/queryClient";
import { queryKeys } from "../../lib/queryKeys";
import {
  getEncoderAhaGuidelinesByIds,
  getEncoderAhaQuarters,
  getEncoderAhaYears,
  getEncoderCatalog,
  getEncoderClinicalCriteriaFile,
  getEncoderClinicalCriteriaFiles,
  getEncoderCmIndexSearch,
  getEncoderCmIndexTree,
  getEncoderCmTable,
  getEncoderCmTabular,
  getEncoderCmTabularSearch,
  getEncoderCmYears,
  getEncoderCoverage,
  getEncoderCoverageLcdDocument,
  getEncoderCoverageStates,
  getEncoderIcdChapters,
  getEncoderIcdGuidelineYears,
  getEncoderIcdGuidelinesByIds,
  getEncoderIcdSubChapters,
  getEncoderIcdSubTopics,
  getEncoderIcdTopics,
  getEncoderMsdrgOptions,
  postEncoderMsdrgMceEdit,
  getEncoderPcsCodeDescription,
  getEncoderPcsDefinitionLookup,
  getEncoderPcsDefinitionsSearch,
  getEncoderPcsIndexSearch,
  getEncoderPcsTables,
  getEncoderPcsYears,
  postEncoderCodeInstructions,
  postEncoderCodeValidate,
  postEncoderCoverageValidate,
  postEncoderCptEdits,
  postEncoderMsdrgElixhauser,
  postEncoderMsdrgGroup,
  postEncoderMsdrgSexRestrictions,
  searchEncoderAhaGuidelines,
  searchEncoderCodes,
  searchEncoderIcdGuidelines,
} from "../../services/encoder";
import type {
  AggregatedIcdCode,
  AhaGuideline,
  ClinicalCriteriaFileContent,
  ClinicalCriteriaFileMeta,
  CmSearchSource,
  CmTabularResult,
  CmTabularSearchMode,
  CodeSearchResult,
  EncoderCatalog,
  EncoderGuidelineType,
  IcdGuideline,
  MedicareCodeEditorRequest,
  MsDrgGroupRequest,
  PcsDefinition,
  PcsIndexSearchResult,
  PcsTableRow,
} from "../../types/encoder";
import {
  DEFAULT_ENCODER_CATALOG,
  extractAggregatedIcdCodes,
  extractAhaGuidelines,
  extractClinicalCriteriaFile,
  extractClinicalCriteriaFiles,
  extractCmIndexTree,
  extractCmSearchHits,
  extractCmTableTrees,
  extractCmTabular,
  extractCmTabularSearchHits,
  extractCodeSearchResults,
  extractEncoderCatalog,
  extractIcdGuidelines,
  extractIcdValidationResults,
  extractCptEditsResults,
  extractPcsCodeDescription,
  extractPcsDefinition,
  extractStringList,
  extractYears,
  uniqueEncoderInstructionCodes,
  extractCmYears,
  GUIDELINE_BY_IDS_CHUNK_SIZE,
  uniqueSortedGuidelineIds,
  unwrapEncoderPayload,
} from "../../utils/encoder";
import {
  extractElixhauserResult,
  extractMedicareCodeEditorResult,
  extractMsDrgGroupResult,
} from "../../utils/encoder-msdrg";

const CATALOG_STALE_MS = 5 * 60 * 1000;
const SEARCH_STALE_MS = 5 * 60 * 1000;

const listField = (
  payload: Record<string, unknown> | null,
  key: string
): unknown[] => {
  const value = payload ? payload[key] : undefined;
  return Array.isArray(value) ? value : [];
};

export const fetchEncoderCatalog = async () => {
  const payload = await getEncoderCatalog();
  return extractEncoderCatalog(payload);
};

export const prefetchEncoderCatalog = () => {
  void queryClient
    .prefetchQuery({
      queryKey: queryKeys.encoderCatalog(),
      queryFn: fetchEncoderCatalog,
      staleTime: CATALOG_STALE_MS,
    })
    .catch((error) => {
      console.error("Failed to prefetch encoder catalog:", error);
    });
};

export const useEncoderCatalog = (options?: { enabled?: boolean }) =>
  useQuery<EncoderCatalog>({
    queryKey: queryKeys.encoderCatalog(),
    queryFn: fetchEncoderCatalog,
    enabled: options?.enabled ?? true,
    staleTime: CATALOG_STALE_MS,
    placeholderData: DEFAULT_ENCODER_CATALOG,
  });

export const searchEncoderCodesQuery = async (params: {
  q: string;
  type?: string;
  year?: number;
  limit?: number;
}) => extractCodeSearchResults(await searchEncoderCodes(params));

export const useEncoderCodeSearch = (
  query: string,
  type: string,
  year: number,
  options?: { enabled?: boolean; limit?: number }
) => {
  const q = query.trim();
  return useQuery<CodeSearchResult[]>({
    queryKey: queryKeys.encoderCodeSearch({
      q,
      type,
      year,
      limit: options?.limit ?? 20,
    }),
    queryFn: () =>
      searchEncoderCodesQuery({
        q,
        type,
        year,
        limit: options?.limit ?? 20,
      }),
    enabled: (options?.enabled ?? true) && q.length > 0,
    staleTime: SEARCH_STALE_MS,
    placeholderData: keepPreviousData,
  });
};

export const useEncoderInstructions = (
  codes: string[],
  options?: { enabled?: boolean }
) => {
  const requestCodes = uniqueEncoderInstructionCodes(codes);
  return useQuery<AggregatedIcdCode[]>({
    queryKey: queryKeys.encoderInstructions(requestCodes),
    queryFn: async () =>
      extractAggregatedIcdCodes(await postEncoderCodeInstructions(requestCodes)),
    enabled: (options?.enabled ?? true) && requestCodes.length > 0,
    staleTime: 60 * 1000,
  });
};

export const useEncoderCodeValidate = () =>
  useMutation({
    mutationFn: async (codes: string[]) => ({
      success: true,
      results: extractIcdValidationResults(await postEncoderCodeValidate(codes)),
    }),
  });

export const useEncoderCptEdits = () =>
  useMutation({
    mutationFn: async (input: { codes: string[]; dateofService: string }) => ({
      success: true,
      results: extractCptEditsResults(
        await postEncoderCptEdits(input.codes, input.dateofService)
      ),
    }),
  });

export const useEncoderCmYears = () =>
  useQuery<{ years: number[]; defaultYear: number }>({
    queryKey: queryKeys.encoderCmYears(),
    queryFn: async () => extractCmYears(await getEncoderCmYears()),
    staleTime: CATALOG_STALE_MS,
  });

export const useEncoderCmIndexSearch = (
  year: number,
  source: CmSearchSource,
  query: string
) => {
  const q = query.trim();
  return useQuery<ReturnType<typeof extractCmSearchHits>>({
    queryKey: queryKeys.encoderCmIndexSearch({ year, source, q }),
    queryFn: async () =>
      extractCmSearchHits(await getEncoderCmIndexSearch({ year, source, q })),
    enabled: q.length >= 2,
    staleTime: 60 * 1000,
  });
};

export const useEncoderCmIndexTree = (
  year: number,
  source: string,
  termId: string | null
) =>
  useQuery<ReturnType<typeof extractCmIndexTree>>({
    queryKey: queryKeys.encoderCmIndexTree({ year, source, termId }),
    queryFn: async () =>
      extractCmIndexTree(
        await getEncoderCmIndexTree({ year, source, termId: termId! })
      ),
    enabled: Boolean(termId),
    staleTime: 60 * 1000,
  });

export const useEncoderCmTable = (
  year: number,
  source: string,
  filter: string,
  enabled: boolean
) =>
  useQuery<ReturnType<typeof extractCmTableTrees>>({
    queryKey: queryKeys.encoderCmTable({ year, source, filter }),
    queryFn: async () =>
      extractCmTableTrees(
        await getEncoderCmTable({ year, source, q: filter || undefined })
      ),
    enabled,
    staleTime: 60 * 1000,
  });

export const useEncoderCmTabular = (year: number, code: string | null) =>
  useQuery<CmTabularResult | null>({
    queryKey: queryKeys.encoderCmTabular({ year, code }),
    queryFn: async () =>
      extractCmTabular(await getEncoderCmTabular({ year, code: code! })),
    enabled: Boolean(code),
    staleTime: 0,
  });

export const useEncoderCmTabularSearch = (
  year: number,
  mode: CmTabularSearchMode,
  query: string
) => {
  const q = query.trim();
  const minLength = mode === "code" ? 1 : 2;
  return useQuery({
    queryKey: queryKeys.encoderCmTabularSearch({ year, mode, q }),
    queryFn: async () =>
      extractCmTabularSearchHits(
        await getEncoderCmTabularSearch({ year, mode, q })
      ),
    enabled: q.length >= minLength,
    staleTime: 30 * 1000,
  });
};

export const useEncoderPcsYears = () =>
  useQuery({
    queryKey: queryKeys.encoderPcsYears(),
    queryFn: async () => {
      const payload = unwrapEncoderPayload(await getEncoderPcsYears());
      const years = extractYears(payload);
      return {
        years,
        defaultYear: Number(payload?.defaultYear ?? years[0] ?? 2026),
        pdfByYear: (payload?.pdfByYear as Record<string, string | null>) ?? {},
      };
    },
    staleTime: CATALOG_STALE_MS,
  });

export const useEncoderPcsTables = (fiscalYear: number, enabled = true) =>
  useQuery({
    queryKey: queryKeys.encoderPcsTables(fiscalYear),
    queryFn: async () => {
      const payload = unwrapEncoderPayload(await getEncoderPcsTables(fiscalYear));
      return {
        version: String(payload?.version ?? ""),
        rows: (payload && Array.isArray(payload.rows)
          ? payload.rows
          : []) as PcsTableRow[],
        definitionCount: Number(payload?.definitionCount ?? 0),
        pdfUrl: typeof payload?.pdfUrl === "string" ? payload.pdfUrl : null,
      };
    },
    enabled,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

export const useEncoderPcsIndexSearch = (fiscalYear: number, query: string) => {
  const q = query.trim();
  return useQuery({
    queryKey: queryKeys.encoderPcsIndexSearch({ fiscalYear, q }),
    queryFn: async () => {
      const payload = unwrapEncoderPayload(
        await getEncoderPcsIndexSearch({ fiscalYear, q, limit: 30 })
      );
      return (
        payload && Array.isArray(payload.results) ? payload.results : []
      ) as PcsIndexSearchResult[];
    },
    enabled: q.length > 0,
    staleTime: 0,
  });
};

export const useEncoderPcsDefinitionsSearch = (
  fiscalYear: number,
  query: string
) => {
  const q = query.trim();
  return useQuery({
    queryKey: queryKeys.encoderPcsDefinitionsSearch({ fiscalYear, q }),
    queryFn: async () => {
      const payload = unwrapEncoderPayload(
        await getEncoderPcsDefinitionsSearch({ fiscalYear, q, limit: 40 })
      );
      return (
        payload && Array.isArray(payload.results) ? payload.results : []
      ) as PcsDefinition[];
    },
    enabled: q.length > 0,
    staleTime: 0,
  });
};

export const useEncoderPcsDefinitionLookup = (
  fiscalYear: number,
  section: string | null,
  pos: number | null,
  label: string | null
) =>
  useQuery({
    queryKey: queryKeys.encoderPcsDefinitionLookup({
      fiscalYear,
      section,
      pos,
      label,
    }),
    queryFn: async () =>
      extractPcsDefinition(
        await getEncoderPcsDefinitionLookup({
          fiscalYear,
          section: section!,
          pos: pos!,
          label: label!,
        })
      ),
    enabled: Boolean(section && pos && label),
    staleTime: 0,
  });

export const useEncoderPcsCodeDescription = (
  fiscalYear: number,
  code: string | null
) =>
  useQuery({
    queryKey: queryKeys.encoderPcsCodeDescription({ fiscalYear, code }),
    queryFn: async () =>
      extractPcsCodeDescription(
        await getEncoderPcsCodeDescription({ fiscalYear, code: code! })
      ),
    enabled: Boolean(code && code.length === 7 && !code.includes("-")),
    staleTime: 0,
  });

export const useEncoderIcdGuidelineYears = (
  guidelineType?: EncoderGuidelineType
) =>
  useQuery<number[]>({
    queryKey: queryKeys.encoderIcdGuidelineYears(guidelineType),
    queryFn: async () =>
      extractYears(await getEncoderIcdGuidelineYears(guidelineType)),
    staleTime: CATALOG_STALE_MS,
  });

export const useEncoderIcdChapters = (guidelineType: EncoderGuidelineType) =>
  useQuery<string[]>({
    queryKey: queryKeys.encoderIcdGuidelineChapters(guidelineType),
    queryFn: async () =>
      extractStringList(await getEncoderIcdChapters(guidelineType), "chapters"),
    staleTime: CATALOG_STALE_MS,
  });

export const useEncoderIcdSubChapters = (
  guidelineType: EncoderGuidelineType,
  chapter: string
) =>
  useQuery<string[]>({
    queryKey: queryKeys.encoderIcdGuidelineSubChapters({
      guidelineType,
      chapter,
    }),
    queryFn: async () =>
      extractStringList(
        await getEncoderIcdSubChapters({ guidelineType, chapter }),
        "subChapters"
      ),
    enabled: Boolean(chapter),
  });

export const useEncoderIcdTopics = (
  guidelineType: EncoderGuidelineType,
  subChapter: string
) =>
  useQuery<string[]>({
    queryKey: queryKeys.encoderIcdGuidelineTopics({ guidelineType, subChapter }),
    queryFn: async () =>
      extractStringList(
        await getEncoderIcdTopics({ guidelineType, subChapter }),
        "topics"
      ),
    enabled: Boolean(subChapter),
  });

export const useEncoderIcdSubTopics = (
  guidelineType: EncoderGuidelineType,
  topic: string
) =>
  useQuery<string[]>({
    queryKey: queryKeys.encoderIcdGuidelineSubTopics({ guidelineType, topic }),
    queryFn: async () =>
      extractStringList(
        await getEncoderIcdSubTopics({ guidelineType, topic }),
        "subTopics"
      ),
    enabled: Boolean(topic),
  });

export const useEncoderIcdGuidelineSearch = (params: {
  guidelineType?: EncoderGuidelineType;
  keyword?: string;
  year?: string;
  chapter?: string;
  subChapter?: string;
  topic?: string;
  subTopic?: string;
  includeBody?: boolean;
  enabled?: boolean;
}) =>
  useQuery<IcdGuideline[]>({
    queryKey: queryKeys.encoderIcdGuidelineSearch(params),
    queryFn: async () => extractIcdGuidelines(await searchEncoderIcdGuidelines(params)),
    enabled: params.enabled ?? Boolean(params.guidelineType),
    placeholderData: keepPreviousData,
  });

const fetchGuidelinesByIdChunks = async <T>(
  ids: Array<number | string>,
  fetcher: (chunk: number[]) => Promise<unknown>,
  extract: (payload: unknown) => T[]
): Promise<T[]> => {
  const unique = uniqueSortedGuidelineIds(ids);
  const all: T[] = [];
  for (let i = 0; i < unique.length; i += GUIDELINE_BY_IDS_CHUNK_SIZE) {
    const chunk = unique.slice(i, i + GUIDELINE_BY_IDS_CHUNK_SIZE);
    const extracted = extract(await fetcher(chunk));
    for (let j = 0; j < extracted.length; j += 1) all.push(extracted[j]);
  }
  return all;
};

export const useEncoderIcdGuidelinesByIds = (
  ids: Array<number | string>,
  enabled = true
) => {
  const normalized = uniqueSortedGuidelineIds(ids);
  return useQuery<IcdGuideline[]>({
    queryKey: queryKeys.encoderIcdGuidelineByIds(normalized.join(",")),
    queryFn: () =>
      fetchGuidelinesByIdChunks(
        normalized,
        getEncoderIcdGuidelinesByIds,
        extractIcdGuidelines
      ),
    enabled: enabled && normalized.length > 0,
  });
};

export const useEncoderAhaYears = () =>
  useQuery<number[]>({
    queryKey: queryKeys.encoderAhaYears(),
    queryFn: async () => extractYears(await getEncoderAhaYears()),
    staleTime: CATALOG_STALE_MS,
  });

export const useEncoderAhaQuarters = (year: string) =>
  useQuery({
    queryKey: queryKeys.encoderAhaQuarters(year),
    queryFn: async () =>
      extractStringList(await getEncoderAhaQuarters(year), "quarters"),
    enabled: Boolean(year),
  });

export const useEncoderAhaSearch = (params: {
  keyword?: string;
  year?: string;
  quarter?: string;
  quarters?: string[];
  includeBody?: boolean;
  cmContext?: boolean;
  referenceCode?: string;
  referenceCategory?: string;
  enabled?: boolean;
}) =>
  useQuery<AhaGuideline[]>({
    queryKey: queryKeys.encoderAhaSearch(params),
    queryFn: async () => extractAhaGuidelines(await searchEncoderAhaGuidelines(params)),
    enabled: params.enabled ?? true,
    placeholderData: keepPreviousData,
  });

export const useEncoderAhaKeywordSearches = (keywords: string[]) =>
  useQuery<AhaGuideline[]>({
    queryKey: queryKeys.encoderAhaKeywordSearches(keywords),
    queryFn: async () => {
      const merged: AhaGuideline[] = [];
      const seen: Record<number, true> = {};
      for (let i = 0; i < keywords.length; i += 1) {
        const rows = extractAhaGuidelines(
          await searchEncoderAhaGuidelines({
            keyword: keywords[i],
            includeBody: true,
          })
        );
        for (let r = 0; r < rows.length; r += 1) {
          const row = rows[r];
          if (!row || seen[row.id]) continue;
          seen[row.id] = true;
          merged.push(row);
        }
      }
      return merged;
    },
    enabled: keywords.length > 0,
  });

export const useEncoderAhaByIds = (
  ids: Array<number | string>,
  enabled = true
) => {
  const normalized = uniqueSortedGuidelineIds(ids);
  return useQuery<AhaGuideline[]>({
    queryKey: queryKeys.encoderAhaByIds(normalized.join(",")),
    queryFn: () =>
      fetchGuidelinesByIdChunks(
        normalized,
        getEncoderAhaGuidelinesByIds,
        extractAhaGuidelines
      ),
    enabled: enabled && normalized.length > 0,
  });
};

export const useEncoderClinicalCriteriaFiles = () =>
  useQuery<ClinicalCriteriaFileMeta[]>({
    queryKey: queryKeys.encoderClinicalCriteriaFiles(),
    queryFn: async () =>
      extractClinicalCriteriaFiles(await getEncoderClinicalCriteriaFiles()),
    staleTime: 5 * 60 * 1000,
  });

export const useEncoderClinicalCriteriaFile = (
  file: ClinicalCriteriaFileMeta | null
) =>
  useQuery<ClinicalCriteriaFileContent | null>({
    queryKey: queryKeys.encoderClinicalCriteriaFile(
      file?.id ?? "",
      file?.name ?? ""
    ),
    queryFn: async () =>
      extractClinicalCriteriaFile(
        await getEncoderClinicalCriteriaFile({
          id: file?.id || undefined,
          name: file?.name || undefined,
        })
      ),
    enabled: Boolean(file?.id || file?.name),
    staleTime: 10 * 60 * 1000,
  });

export const useEncoderMsdrgOptions = () =>
  useQuery({
    queryKey: queryKeys.encoderMsdrgOptions(),
    queryFn: async () => {
      const payload = unwrapEncoderPayload(await getEncoderMsdrgOptions());
      return payload;
    },
    staleTime: CATALOG_STALE_MS,
  });

export const useEncoderMsdrgGroup = () =>
  useMutation({
    mutationFn: async (body: MsDrgGroupRequest) =>
      extractMsDrgGroupResult(await postEncoderMsdrgGroup(body)),
  });

export const useEncoderMsdrgSexRestrictions = () =>
  useMutation({
    mutationFn: async (body: {
      diagnosisCodes: string[];
      procedureCodes: string[];
    }) => {
      const payload = unwrapEncoderPayload(
        await postEncoderMsdrgSexRestrictions(body)
      );
      return {
        success: payload?.success !== false,
        error: typeof payload?.error === "string" ? payload.error : undefined,
        disclaimer:
          typeof payload?.disclaimer === "string" ? payload.disclaimer : "",
        diagnosisCodes: listField(payload, "diagnosisCodes"),
        procedureCodes: listField(payload, "procedureCodes"),
      };
    },
  });

export const useEncoderMsdrgElixhauser = () =>
  useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      extractElixhauserResult(await postEncoderMsdrgElixhauser(body)),
  });

export const useEncoderMsdrgMceEdit = () =>
  useMutation({
    mutationFn: async (body: MedicareCodeEditorRequest) =>
      extractMedicareCodeEditorResult(await postEncoderMsdrgMceEdit(body)),
  });

export const useEncoderCoverageStates = () =>
  useQuery({
    queryKey: queryKeys.encoderCoverageStates(),
    queryFn: async () => {
      const payload = unwrapEncoderPayload(await getEncoderCoverageStates());
      const list = listField(payload, "data");
      return (list.length ? list : listField(payload, "states")) as Array<{
        abbreviation: string;
        name: string;
      }>;
    },
    staleTime: 60 * 60 * 1000,
  });

export const useEncoderCoverage = (code: string, state?: string) =>
  useQuery({
    queryKey: queryKeys.encoderCoverage({ code, state: state ?? "All" }),
    queryFn: async () => {
      const payload = unwrapEncoderPayload(
        await getEncoderCoverage({ code, state })
      );
      const nested =
        payload && typeof payload.data === "object" && payload.data
          ? (payload.data as { code?: string; articles?: unknown[] })
          : undefined;
      const articles =
        nested && Array.isArray(nested.articles)
          ? nested.articles
          : listField(payload, "articles");
      return {
        success: payload?.success !== false,
        error: typeof payload?.error === "string" ? payload.error : undefined,
        code: nested?.code ?? code,
        articles,
      };
    },
    enabled: code.trim().length > 0,
    staleTime: SEARCH_STALE_MS,
  });

export const useEncoderCoverageValidate = () =>
  useMutation({
    mutationFn: async (body: {
      procedures: string[];
      diagnoses: string[];
      state?: string;
    }) => unwrapEncoderPayload(await postEncoderCoverageValidate(body)),
  });

export const useEncoderLcdDocument = () =>
  useMutation({
    mutationFn: async (lcdId: string) => {
      const payload = unwrapEncoderPayload(
        await getEncoderCoverageLcdDocument(lcdId)
      );
      return typeof payload?.url === "string" ? payload.url : null;
    },
  });
